<template>
  <div>
    <b-row class="mb-4">
      <b-col :xs="12">
        <page-header :title="$t('account.index.currencySettings.heading')" />
      </b-col>
    </b-row>
    <b-row>
      <b-col :sm="12" :lg="9">
        <div class="currency-settings-wrapper" v-if="isRemittanceApiLoaded">
          <div v-if="!isDripSwitchEnabled">
            <conversion-rate-details
              :hideKnowMore="true"
              :isInrTransactionCompleted="
                $store.state.root.isInrTransactionCompleted
              "
              @openDialog="showINRDialog = true"
            />
            <benefits />
          </div>
          <inr-payment-enabled
            v-else
            :onStatusToggleSuccess="onINRDisabledSuccess"
          />
          <disabled-message-modal
            :userLevel="true"
            :showDialog="showDisabledMessageModal"
            @closeDialog="onCloseDMMDialog"
          />
          <invoice-settings-table
            :invoices="invoices"
            :invoiceMeta="invoiceMeta"
            :loading="loading"
            :pagination="paginationVars"
            :current-page="currentPage"
            @scrollBottom="loadNextPage"
            @refreshInvoices="refreshInvoices"
          />
        </div>
        <div v-else>
          <loader :height="200" />
        </div>
      </b-col>
      <b-col :sm="12" :lg="3">
        <rate-conversion-widget
          :rate="conversionRatesData.rate"
          :isFulfilled="isRateConversionApiFulfilled"
          :isINREnabled="isDripSwitchEnabled"
        />
        <div class="mb-3"></div>
        <update-bank-rate-widget
          v-if="isRateConversionApiFulfilled && !isDripSwitchEnabled"
        />
        <div class="mb-3"></div>
        <faq-question-widget />
        <div class="mb-5"></div>
      </b-col>
    </b-row>
    <enable-inr-universal-modal
      :showINRDialog="showINRDialog"
      @closeDialog="closeDialog"
      @cancelDialog="cancelDialog"
    />
  </div>
</template>

<script>
import Loader from '@/components/common/Loader'
import Benefits from '@/components/account/CurrencySettings/Benefits'
import FaqQuestionWidget from '@/components/account/CurrencySettings/FaqQuestionWidget'
import InrPaymentEnabled from '@/components/account/CurrencySettings/InrPaymentEnabled'
import EnableInrUniversalModal from '@/components/account/CurrencySettings/EnableInrUniversalModal'
import RateConversionWidget from '@/components/dashboard/RateConversionWidget'
import ConversionRateDetails from '@/components/drip-switch/ConversionRateDetails'
import moment from 'moment'
import exportersService from '@/services/exporters'
import conversionRateService from '@/services/conversionRate'
import {
  RATE_CONVERSION_REFETCH_DEFAULT_TIME,
  MAXIMUM_SET_TIMEOUT_DELAY
} from '@/utils/constants'
import PageHeader from '@/components/common/PageHeader'
import InvoiceSettingsTable from '@/components/account/CurrencySettings/InvoiceSettingsTable'
import invoiceService from '@/services/invoice'
import UpdateBankRateWidget from '@/components/dashboard/UpdateBankRateWidget'
import DisabledMessageModal from '@/components/drip-switch/DisabledMessageModal'

export default {
  data() {
    return {
      conversionRatesData: {
        rate: '',
        from_currency: '',
        to_currency: '',
        valid_till: ''
      },
      timeOutID: '',
      isDripSwitchEnabled: null,
      isRemittanceApiLoaded: false,
      loadingRemittanceAPI: false,
      isRateConversionApiFulfilled: false,
      // invoice table
      sortBy: 'created_at',
      sortDesc: true,
      searchTerm: '',
      invoices: [],
      loading: false,
      pageLoading: false,
      filters: this.$route.params.selectedFilter
        ? [this.$route.params.selectedFilter]
        : ['processing', 'approved', 'pending'],
      currentPage: 1,
      paginationVars: {
        per_page: 20,
        total_objects: 8,
        total_pages: 1
      },
      invoiceMeta: null,
      pendingTasks: [],
      alertFilters: null,
      showINRDialog: false,
      showDisabledMessageModal: false
    }
  },
  components: {
    Benefits,
    RateConversionWidget,
    ConversionRateDetails,
    PageHeader,
    InvoiceSettingsTable,
    FaqQuestionWidget,
    InrPaymentEnabled,
    UpdateBankRateWidget,
    Loader,
    EnableInrUniversalModal,
    DisabledMessageModal
  },
  mounted() {
    this.scrollToTop()
    this.$mpTimeEvent('Currency Settings Active Duration')
    this.$mpTrack('Currency Settings Page Enter')
    this.getConversionRate()
    this.fetchRemittanceStatus()
    this.loadCounters()
    this.fetchInvoices(false)
  },
  methods: {
    onCloseDMMDialog() {
      this.showDisabledMessageModal = false
    },
    onINRDisabledSuccess() {
      this.showDisabledMessageModal = true
      this.fetchRemittanceStatus()
      this.refreshInvoices()
    },
    closeDialog() {
      this.showINRDialog = false
      this.fetchRemittanceStatus()
      this.refreshInvoices()
    },
    cancelDialog() {
      this.showINRDialog = false
    },
    scrollToTop() {
      window.scrollTo(0, 0)
    },
    refreshInvoices() {
      this.fetchInvoices(false)
      this.currentPage = 1
      this.paginationVars = {
        per_page: 20,
        total_objects: 8,
        total_pages: 1
      }
    },
    loadNextPage() {
      this.currentPage++
      this.fetchInvoices(true)
    },
    loadCounters() {
      return exportersService
        .getInvoiceMeta(this.$store.state.account.accountId)
        .then((res) => {
          this.invoiceMeta = res.data
        })
        .catch(console.error)
    },
    async getConversionRate() {
      this.isRateConversionApiFulfilled = false
      const exporterId = this.findExporter(
        this.$store.state.account.accountId
      ).id

      try {
        const { data } = await conversionRateService.getConversionRate(
          exporterId
        )
        this.conversionRatesData = data
        this.isRateConversionApiFulfilled = true
        let timeLeft = 0
        timeLeft = moment(this.conversionRatesData.valid_till).diff(new Date())
        let bufferAddedToTimeLeft = timeLeft + 100 // 100ms buffer added before making another API call so that we always fetch the newer data
        if (
          !bufferAddedToTimeLeft ||
          bufferAddedToTimeLeft < 0 ||
          bufferAddedToTimeLeft > MAXIMUM_SET_TIMEOUT_DELAY
        ) {
          bufferAddedToTimeLeft = RATE_CONVERSION_REFETCH_DEFAULT_TIME
        }
        clearTimeout(this.timeOutID)
        this.timeOutID = setTimeout(() => {
          this.getConversionRate()
        }, bufferAddedToTimeLeft)
      } catch (err) {
        this.isRateConversionApiFulfilled = true
        clearTimeout(this.timeOutID)
        this.timeOutID = setTimeout(() => {
          this.getConversionRate()
        }, RATE_CONVERSION_REFETCH_DEFAULT_TIME)
        console.log(err)
      }
    },
    fetchRemittanceStatus() {
      const accountId = this.$store.state.account.accountId
      this.isRemittanceApiLoaded = false
      return exportersService
        .fetchExporterMetaData(accountId, {
          include: ['customer_pending_task_flows']
        })
        .then((res) => {
          this.pendingTasks = res.data.customer_pending_task_flows
          this.isDripSwitchEnabled = res.data.drip_switch_enabled
          this.isRemittanceApiLoaded = true
        })
        .catch((err) => {
          this.isRemittanceApiLoaded = true
          console.log(err)
        })
    },
    findExporter(exporterId) {
      return this.$store.getters.user.linked_users.find((exporter) => {
        return parseInt(exporter.id) === parseInt(exporterId)
      })
    },
    fetchInvoiceQuery() {
      if (this.alertFilters) {
        return invoiceService.fetch(
          this.$store.state.account.accountId,
          {
            filters: this.alertFilters,
            sort: this.sortParams
          },
          this.currentPage,
          this.paginationVars.per_page,
          'Business::InvoiceListSerializer'
        )
      } else if (this.searchTerm !== '') {
        return invoiceService.fetch(
          this.$store.state.account.accountId,
          {
            search: this.searchTerm,
            sort: this.sortParams
          },
          this.currentPage,
          this.paginationVars.per_page,
          'Business::InvoiceListSerializer'
        )
      }
      return invoiceService.fetch(
        this.$store.state.account.accountId,
        {
          filters: this.filters,
          sort: this.sortParams
        },
        this.currentPage,
        this.paginationVars.per_page,
        'Business::InvoiceListSerializer'
      )
    },
    fetchInvoices(append = false) {
      if (!append) {
        this.loading = true
      }
      return this.fetchInvoiceQuery()
        .then(async (res) => {
          const newInvoices = this.filters.includes('advanced')
            ? await this.appendAmountInInrValue(
                res.data['drip/business/invoices']
              )
            : res.data['drip/business/invoices']

          this.paginationVars = res.data.meta.pagination
          if (append) {
            this.invoices = JSON.parse(
              JSON.stringify(this.invoices.concat(newInvoices))
            )
          } else {
            this.invoices = newInvoices
          }
          this.loading = false
        })
        .catch((err) => {
          this.loading = false
          console.error(err)
        })
    }
  },
  beforeDestroy() {
    this.$mpTrack('Currency Settings Active Duration')
    this.$mpTrack('Currency Settings Page Exit')
    clearTimeout(this.timeOutID)
  }
}
</script>

<style lang="scss">
@import '@/assets/scss/_palette.scss';

.currency-settings-wrapper {
  background-color: white;

  .sub-heading {
    font-size: 1.6rem;
    color: map-get($noble-blue, 500);
    font-weight: bold;
  }
}
</style>
